import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Temporal } from 'temporal-polyfill';

import { getCoachTeachingHours, getUser } from '@/lib/api-calls';
import { QueryKeys } from '@/lib/queries';
import { validateRouteRole } from '@/lib/routing';
import { airColumnHelper } from '@/lib/tables';

import { InfoPage } from '@/components/info-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';

import { FrequentTimeZones, isAirAdmin } from '@airreading/common';
import { WeeklyTeachingHoursWeekDto, weeklyTeachingHoursWeekSchema } from '@airreading/common/data';

import { coachesTabs } from '../-components/tabs';

const columnHelper = airColumnHelper<WeeklyTeachingHoursWeekDto>(weeklyTeachingHoursWeekSchema.keyof());

function formatWeekDate(date: Temporal.PlainDate): string {
  return `${date.month}/${date.day}/${date.year}`;
}

function formatWeekPeriod(weekStart: string, weekEnd: string): string {
  const start = Temporal.Instant.from(weekStart).toZonedDateTimeISO(FrequentTimeZones.UsPacific).toPlainDate();
  const end = Temporal.Instant.from(weekEnd).toZonedDateTimeISO(FrequentTimeZones.UsPacific).toPlainDate();
  return `${formatWeekDate(start)} - ${formatWeekDate(end)}`;
}

function formatWeeklyTeachingHours(totalMinutes: number): string {
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) {
    return `${wholeHours} hrs`;
  }
  return `${wholeHours} hrs ${minutes} mins`;
}

const weeklyTeachingHoursColumns = [
  columnHelper.accessor((row) => row, {
    id: 'weekStart',
    header: 'Week period',
    cell: ({ getValue }) => {
      const { weekStart, weekEnd } = getValue();
      return formatWeekPeriod(weekStart, weekEnd);
    },
    enableSorting: false,
  }),
  columnHelper.accessor('sessions', {
    id: 'sessions',
    header: 'Sessions',
    cell: ({ getValue }) => getValue(),
    enableSorting: false,
  }),
  columnHelper.accessor('minutes', {
    id: 'minutes',
    header: 'Weekly teaching hours',
    cell: ({ getValue }) => formatWeeklyTeachingHours(getValue()),
    enableSorting: false,
  }),
];

export const Route = createFileRoute('/_layout/teachers/$teacherId/weekly-teaching-hours')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: WeeklyTeachingHoursPage,
});

function WeeklyTeachingHoursPage() {
  const { teacherId } = Route.useParams();
  const { data: user } = useSuspenseQuery({
    queryKey: QueryKeys.getUser(teacherId),
    queryFn: () => getUser(teacherId),
  });

  const { data, isPending, refetch, error } = useQuery(getCoachTeachingHours({ coachId: teacherId }));

  const table = useReactTable({
    data: data?.weeks ?? [],
    columns: weeklyTeachingHoursColumns,
    getRowId: (row) => row.weekStart,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <InfoPage
      headerText={user.fullNameOrEmail}
      tabs={coachesTabs({ teacherId })}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Teachers',
            link: {
              to: '/teachers',
            },
          },
        ],
        currentPage: user.fullNameOrEmail ?? '',
      }}
    >
      <EntityTable
        table={table}
        isLoading={isPending}
        hasError={!!error}
        emptyState="No teaching hours found"
        errorState={<TableErrorState refetchAction={refetch} />}
      />
    </InfoPage>
  );
}
